import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import {
    Heart, Brain, Activity, Timer, Moon, Flower2, Globe, ArrowUpRight, ChevronDown, ChevronUp, Compass,
} from "lucide-react";
import { Link } from "react-router-dom";

const ICONS = [Heart, Brain, Activity, Timer, Moon, Flower2, Globe];

const PROTOCOL_LAYOUT = [
    "md:col-span-2",
    "md:col-span-2",
    "md:col-span-1",
    "md:col-span-2",
    "md:col-span-1",
    "md:col-span-2",
    "md:col-span-2",
];

const ProtocolCard = ({ protocol, idx, t }) => {
    const Icon = ICONS[idx];
    return (
        <Link
            to={`/protocollo/${protocol.slug}`}
            data-testid={`protocol-card-${protocol.id}`}
            className={`relative group bg-white p-6 md:p-8 lg:p-9 flex flex-col justify-between min-h-[260px] md:min-h-[290px] transition-all duration-500 hover:bg-black hover:text-white ${PROTOCOL_LAYOUT[idx]}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-baseline gap-3 min-w-0">
                    <span className="font-display font-bold text-2xl text-fw tabular-nums leading-none">
                        {protocol.id}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-black/40 group-hover:text-white/50 transition-colors leading-none mt-1">
                        {t.method.protocolLabel}
                    </span>
                </div>
                <div className="w-11 h-11 rounded-sm bg-black/5 group-hover:bg-fw flex items-center justify-center transition-colors duration-300 shrink-0">
                    {Icon && (
                        <Icon className="w-5 h-5 text-black group-hover:text-black transition-colors duration-300" strokeWidth={1.75} />
                    )}
                </div>
            </div>

            <div className="mt-10">
                <h3
                    data-testid={`protocol-name-${protocol.id}`}
                    className="font-display font-bold text-lg md:text-xl lg:text-2xl leading-[1.1] tracking-tight mb-3 uppercase break-words hyphens-auto"
                    lang="it"
                >
                    {protocol.name}
                </h3>
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-fw-700 group-hover:text-fw mb-4">
                    {protocol.tagline}
                </p>
                <p className="text-sm text-black/70 group-hover:text-white/70 leading-relaxed transition-colors">
                    {protocol.description}
                </p>
            </div>

            <div className="mt-8 pt-5 border-t border-black/10 group-hover:border-white/20 flex items-center justify-between transition-colors">
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-black/50 group-hover:text-white/55">
                    {t.method.discoverMore}
                </span>
                <ArrowUpRight
                    className="w-4 h-4 transition-all duration-300 group-hover:text-fw group-hover:translate-x-1 group-hover:-translate-y-1"
                    strokeWidth={2}
                />
            </div>
        </Link>
    );
};

/**
 * Filler card that occupies the empty grid cells next to protocol 3
 * when in compact mode (3 visible). It both fills the visual gap and
 * gives a clear "next step" CTA: take the quiz. Hidden when all 7 are shown.
 */
const ProtocolsFiller = ({ t }) => (
    <Link
        to="/quiz"
        data-testid="protocols-filler-quiz"
        className="relative group bg-black text-white p-6 md:p-8 lg:p-9 flex flex-col justify-between min-h-[260px] md:min-h-[290px] overflow-hidden transition-all duration-500 hover:glow-green md:col-span-3"
    >
        <span className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
        <span className="absolute -top-24 -right-24 w-[360px] h-[360px] rounded-full bg-fw/15 blur-[140px] pointer-events-none" />
        <span className="absolute left-0 top-0 bottom-0 w-1 bg-fw" aria-hidden="true" />

        <div className="relative flex items-start justify-between gap-3">
            <div className="flex flex-col">
                <span className="font-heavy text-[40px] md:text-[48px] text-fw leading-none mb-1">?</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/55">
                    {t.quizCta.overline}
                </span>
            </div>
            <div className="w-11 h-11 rounded-sm bg-fw/15 border border-fw/40 flex items-center justify-center shrink-0">
                <Compass className="w-5 h-5 text-fw" strokeWidth={2} />
            </div>
        </div>

        <div className="relative mt-10">
            <h3 className="font-display font-bold text-lg md:text-xl lg:text-2xl leading-[1.1] tracking-tight mb-3 uppercase text-white">
                {t.quizCta.headline}
            </h3>
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-fw mb-4">
                6 domande · 2 minuti
            </p>
            <p className="text-sm text-white/70 leading-relaxed max-w-md">
                {t.quizCta.description}
            </p>
        </div>

        <div className="relative mt-8 pt-5 border-t border-white/15 flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-fw">
                {t.quizCta.cta}
            </span>
            <ArrowUpRight
                className="w-4 h-4 text-fw transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                strokeWidth={2}
            />
        </div>
    </Link>
);

export const Protocols = () => {
    const { t } = useLanguage();
    const [showAll, setShowAll] = useState(false);
    const INITIAL = 3;
    const protocolsToShow = showAll ? t.protocols : t.protocols.slice(0, INITIAL);
    const remaining = t.protocols.length - INITIAL;

    return (
        <section
            id="method"
            data-testid="method-section"
            className="relative bg-white text-black py-16 md:py-20 lg:py-24"
        >
            <div className="mx-auto max-w-[1440px] px-5 md:px-10 lg:px-16">
                <div className="grid grid-cols-12 gap-6 mb-14 md:mb-20">
                    <div className="col-span-12 md:col-span-6">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-10 h-px bg-fw" />
                            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-fw-700">
                                {t.method.overline}
                            </span>
                        </div>
                        <h2
                            data-testid="method-headline"
                            className="font-heavy text-5xl md:text-6xl lg:text-7xl leading-[0.95] uppercase text-balance"
                        >
                            {t.method.headline}
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-5 md:col-start-8 flex items-end">
                        <p
                            data-testid="method-description"
                            className="text-base md:text-lg text-black/70 leading-relaxed max-w-xl"
                        >
                            {t.method.description}
                        </p>
                    </div>
                </div>

                <div
                    id="protocols"
                    data-testid="protocols-grid"
                    className="grid grid-cols-1 md:grid-cols-4 gap-px bg-black/10"
                >
                    {protocolsToShow.map((protocol, idx) => (
                        <ProtocolCard key={protocol.id} protocol={protocol} idx={idx} t={t} />
                    ))}
                    {!showAll && <ProtocolsFiller t={t} />}
                </div>

                {remaining > 0 && (
                    <div className="mt-8 md:mt-10 flex justify-center">
                        <button
                            type="button"
                            onClick={() => setShowAll((v) => !v)}
                            data-testid="protocols-show-all"
                            className="group inline-flex items-center gap-3 px-6 py-3.5 border border-black/15 bg-white text-black font-display font-semibold text-[11px] uppercase tracking-[0.22em] rounded-sm hover:border-fw hover:text-fw transition-all duration-300"
                        >
                            {showAll ? (
                                <>
                                    <ChevronUp className="w-4 h-4" strokeWidth={2} />
                                    {t.common.showLess}
                                </>
                            ) : (
                                <>
                                    <ChevronDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" strokeWidth={2} />
                                    {t.common.showAllProtocols}
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};
